import React, { useEffect, useState, useCallback } from "react";
import {
  Target, Star, MessageCircle, Users, CalendarRange, Scale, LayoutDashboard,
  Plus, Send, CheckCircle2, X, TrendingUp, ClipboardCheck,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, Avatar, fmtDate, EmptyState } from "../components/ui";

const STAGE_TONE = {
  SELF_PENDING: "warn", SELF_SUBMITTED: "info", MANAGER_SUBMITTED: "info",
  CALIBRATED: "ok", SIGNED_OFF: "ok",
};
const RATE_COLORS = ["#ef4444", "#f59e0b", "#64748b", "#0ea5e9", "#10b981"];

function RatingPicker({ value, onChange, scale, disabled, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      {scale.map((s) => (
        <button
          key={s.value} type="button" disabled={disabled} title={s.label}
          onClick={() => onChange(s.value)}
          data-testid={testid ? `${testid}-${s.value}` : undefined}
          className={`h-8 w-8 rounded-md border text-sm font-semibold transition-colors ${
            value === s.value
              ? "border-accent bg-accent text-white"
              : "border-slate-200 text-slate-500 hover:border-accent/50 dark:border-slate-700 dark:text-slate-400"
          } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
        >{s.value}</button>
      ))}
    </div>
  );
}

function ScoreChip({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/50">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="font-mono text-lg font-bold text-slate-800 dark:text-slate-100">{value ?? "—"}</p>
    </div>
  );
}

/* ---------------- Appraisal Drawer (self + manager + calibration) ---------------- */
function AppraisalDrawer({ id, meta, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [goalR, setGoalR] = useState([]);
  const [compR, setCompR] = useState([]);
  const [selfC, setSelfC] = useState("");
  const [mgrC, setMgrC] = useState("");
  const [cal, setCal] = useState({ finalRating: "", potential: "MEDIUM", calibrationComment: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/pms/appraisals/${id}`).then(({ data }) => {
    setD(data);
    setGoalR(data.goalRatings.map((g) => ({ ...g })));
    setCompR(data.competencyRatings.map((c) => ({ ...c })));
    setSelfC(data.selfOverallComment || "");
    setMgrC(data.mgrOverallComment || "");
    setCal({ finalRating: data.finalRating ?? "", potential: data.potential || "MEDIUM", calibrationComment: data.calibrationComment || "" });
  }), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Modal open onClose={onClose} title="Appraisal"><Loading /></Modal>;

  const stage = d.stage;
  const selfEditable = d.isSelf && ["SELF_PENDING", "SELF_SUBMITTED"].includes(stage);
  const mgrEditable = d.isManager && ["SELF_SUBMITTED", "MANAGER_SUBMITTED"].includes(stage);
  const calEditable = meta.canManage && ["MANAGER_SUBMITTED", "CALIBRATED"].includes(stage);
  const scale = meta.ratingScale;

  const setG = (gid, field, val) => setGoalR((p) => p.map((g) => (g.goalId === gid ? { ...g, [field]: val } : g)));
  const setC = (key, field, val) => setCompR((p) => p.map((c) => (c.key === key ? { ...c, [field]: val } : c)));

  const submitSelf = async (submit) => {
    setBusy(true);
    try {
      await api.post(`/pms/appraisals/${id}/self`, {
        goalRatings: goalR.map((g) => ({ goalId: g.goalId, selfRating: g.selfRating, selfComment: g.selfComment })),
        competencyRatings: compR.map((c) => ({ key: c.key, selfRating: c.selfRating, selfComment: c.selfComment })),
        selfOverallComment: selfC, submit,
      });
      alert(submit ? "Self-appraisal submitted" : "Draft saved");
      load(); onSaved?.();
    } catch (e) { alert(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const submitMgr = async (submit) => {
    setBusy(true);
    try {
      await api.post(`/pms/appraisals/${id}/manager`, {
        goalRatings: goalR.map((g) => ({ goalId: g.goalId, mgrRating: g.mgrRating, mgrComment: g.mgrComment })),
        competencyRatings: compR.map((c) => ({ key: c.key, mgrRating: c.mgrRating, mgrComment: c.mgrComment })),
        mgrOverallComment: mgrC, submit,
      });
      alert(submit ? "Manager review submitted" : "Draft saved");
      load(); onSaved?.();
    } catch (e) { alert(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const calibrate = async () => {
    if (cal.finalRating === "") return alert("Select a final rating");
    setBusy(true);
    try {
      await api.post(`/pms/appraisals/${id}/calibrate`, { finalRating: +cal.finalRating, potential: cal.potential, calibrationComment: cal.calibrationComment });
      alert("Calibrated"); load(); onSaved?.();
    } catch (e) { alert(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const signoff = async () => {
    setBusy(true);
    try { await api.post(`/pms/appraisals/${id}/signoff`); alert("Signed off"); load(); onSaved?.(); }
    catch (e) { alert(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose}
      title={<div className="flex items-center gap-2"><span>{d.employeeName}</span><Badge value={d.stageLabel} tone={STAGE_TONE[stage]} /></div>}>
      <div className="space-y-5" data-testid="appraisal-detail">
        <div className="flex flex-wrap gap-2 text-xs text-slate-500 dark:text-slate-400">
          <span>Cycle: <b className="text-slate-700 dark:text-slate-200">{d.cycle?.name}</b></span>
          <span>· Manager: {d.managerName || "—"}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <ScoreChip label="Self Score" value={d.scores.selfScore} />
          <ScoreChip label="Manager Score" value={d.scores.mgrScore} />
          <ScoreChip label="Final Rating" value={d.finalRating} />
        </div>

        {/* Goals */}
        <div>
          <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200"><Target size={15} /> Goals ({goalR.length})</h4>
          {goalR.length === 0 ? <p className="text-xs text-slate-400">No goals set for this cycle.</p> : (
            <div className="space-y-3">
              {goalR.map((g) => (
                <div key={g.goalId} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700" data-testid={`drawer-goal-${g.goalId}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div><p className="text-sm font-medium text-slate-800 dark:text-slate-100">{g.title}</p>
                      <p className="text-[11px] text-slate-400">{g.category} · weight {g.weightage}% · progress {g.progress}%</p></div>
                  </div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <div><p className="mb-1 text-[11px] font-semibold uppercase text-slate-400">Self</p>
                      <RatingPicker value={g.selfRating} scale={scale} disabled={!selfEditable} onChange={(v) => setG(g.goalId, "selfRating", v)} testid={`goal-self-${g.goalId}`} />
                    </div>
                    <div><p className="mb-1 text-[11px] font-semibold uppercase text-slate-400">Manager</p>
                      <RatingPicker value={g.mgrRating} scale={scale} disabled={!mgrEditable} onChange={(v) => setG(g.goalId, "mgrRating", v)} testid={`goal-mgr-${g.goalId}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Competencies */}
        <div>
          <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200"><Star size={15} /> Competencies</h4>
          <div className="space-y-3">
            {compR.map((c) => (
              <div key={c.key} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700" data-testid={`drawer-comp-${c.key}`}>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{c.name}</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <div><p className="mb-1 text-[11px] font-semibold uppercase text-slate-400">Self</p>
                    <RatingPicker value={c.selfRating} scale={scale} disabled={!selfEditable} onChange={(v) => setC(c.key, "selfRating", v)} testid={`comp-self-${c.key}`} /></div>
                  <div><p className="mb-1 text-[11px] font-semibold uppercase text-slate-400">Manager</p>
                    <RatingPicker value={c.mgrRating} scale={scale} disabled={!mgrEditable} onChange={(v) => setC(c.key, "mgrRating", v)} testid={`comp-mgr-${c.key}`} /></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* overall comments */}
        {(d.isSelf || selfEditable) && (
          <div><label className="label">Self summary</label>
            <textarea data-testid="self-overall" disabled={!selfEditable} value={selfC} onChange={(e) => setSelfC(e.target.value)} className="input h-16 py-2" /></div>
        )}
        {(d.isManager || d.mgrOverallComment) && (
          <div><label className="label">Manager summary</label>
            <textarea data-testid="mgr-overall" disabled={!mgrEditable} value={mgrC} onChange={(e) => setMgrC(e.target.value)} className="input h-16 py-2" /></div>
        )}

        {/* calibration panel */}
        {(calEditable || d.calibrationComment) && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 dark:border-emerald-500/30 dark:bg-emerald-500/5">
            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-300"><Scale size={15} /> Calibration</h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><label className="label">Final Rating</label>
                <select disabled={!calEditable} data-testid="cal-final" value={cal.finalRating} onChange={(e) => setCal({ ...cal, finalRating: e.target.value })} className="input">
                  <option value="">—</option>{scale.map((s) => <option key={s.value} value={s.value}>{s.value} · {s.label}</option>)}</select></div>
              <div><label className="label">Potential</label>
                <select disabled={!calEditable} data-testid="cal-potential" value={cal.potential} onChange={(e) => setCal({ ...cal, potential: e.target.value })} className="input">
                  {meta.potentialOptions.map((p) => <option key={p} value={p}>{p}</option>)}</select></div>
            </div>
            <div className="mt-2"><label className="label">Calibration note</label>
              <textarea disabled={!calEditable} data-testid="cal-comment" value={cal.calibrationComment} onChange={(e) => setCal({ ...cal, calibrationComment: e.target.value })} className="input h-14 py-2" /></div>
          </div>
        )}

        {/* actions */}
        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4 dark:border-slate-800">
          {selfEditable && <>
            <button disabled={busy} onClick={() => submitSelf(false)} className="btn-outline" data-testid="self-save">Save draft</button>
            <button disabled={busy} onClick={() => submitSelf(true)} className="btn-accent" data-testid="self-submit"><Send size={15} /> Submit Self-Appraisal</button>
          </>}
          {mgrEditable && <>
            <button disabled={busy} onClick={() => submitMgr(false)} className="btn-outline" data-testid="mgr-save">Save draft</button>
            <button disabled={busy} onClick={() => submitMgr(true)} className="btn-accent" data-testid="mgr-submit"><Send size={15} /> Submit Manager Review</button>
          </>}
          {calEditable && stage === "MANAGER_SUBMITTED" && <button disabled={busy} onClick={calibrate} className="btn-accent" data-testid="cal-submit"><Scale size={15} /> Save Calibration</button>}
          {meta.canManage && stage === "CALIBRATED" && <button disabled={busy} onClick={signoff} className="btn-accent" data-testid="signoff-btn"><CheckCircle2 size={15} /> Sign Off</button>}
          {!selfEditable && !mgrEditable && !calEditable && stage !== "CALIBRATED" && <span className="text-xs text-slate-400">No actions available at this stage.</span>}
        </div>
      </div>
    </Modal>
  );
}

/* ---------------- Goal modal ---------------- */
function GoalModal({ open, onClose, onSaved, meta, cycles }) {
  const [g, setG] = useState({ employeeId: "", title: "", category: "KRA", weightage: 20, cycleId: "", description: "", metric: "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/pms/goals", g); alert("Goal added"); onClose(); onSaved(); setG({ employeeId: "", title: "", category: "KRA", weightage: 20, cycleId: "", description: "", metric: "" }); }
    catch (err) { alert(apiError(err.response?.data?.detail)); }
  };
  const canPickEmp = meta.canManage || meta.role === "MANAGER";
  return (
    <Modal open={open} onClose={onClose} title="New Goal"
      footer={<><button onClick={onClose} className="btn-outline">Cancel</button><button form="goal-form" className="btn-accent">Create</button></>}>
      <form id="goal-form" onSubmit={save} className="space-y-4">
        {canPickEmp && <div><label className="label">Employee</label>
          <select required data-testid="goal-emp" value={g.employeeId} onChange={(e) => setG({ ...g, employeeId: e.target.value })} className="input">
            <option value="">— Select (self if blank) —</option>{meta.employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</select></div>}
        <div className="grid grid-cols-2 gap-4">
          <div><label className="label">Title *</label><input required data-testid="goal-title" value={g.title} onChange={(e) => setG({ ...g, title: e.target.value })} className="input" /></div>
          <div><label className="label">Category</label><select data-testid="goal-cat" value={g.category} onChange={(e) => setG({ ...g, category: e.target.value })} className="input">{meta.goalCategories.map((c) => <option key={c}>{c}</option>)}</select></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="label">Weightage %</label><input type="number" data-testid="goal-weight" value={g.weightage} onChange={(e) => setG({ ...g, weightage: +e.target.value })} className="input" /></div>
          <div><label className="label">Cycle</label><select data-testid="goal-cycle" value={g.cycleId} onChange={(e) => setG({ ...g, cycleId: e.target.value })} className="input"><option value="">— None —</option>{cycles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        </div>
        <div><label className="label">Success metric</label><input data-testid="goal-metric" value={g.metric} onChange={(e) => setG({ ...g, metric: e.target.value })} className="input" /></div>
        <div><label className="label">Description</label><textarea data-testid="goal-desc" value={g.description} onChange={(e) => setG({ ...g, description: e.target.value })} className="input h-16 py-2" /></div>
      </form>
    </Modal>
  );
}

/* ---------------- Main page ---------------- */
export default function Performance() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [appraisals, setAppraisals] = useState([]);
  const [goals, setGoals] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [drawerId, setDrawerId] = useState(null);
  const [goalOpen, setGoalOpen] = useState(false);
  const [fbOpen, setFbOpen] = useState(false);
  const [fb, setFb] = useState({ toId: "", content: "", rating: 5 });
  const [cycleOpen, setCycleOpen] = useState(false);
  const [newCycle, setNewCycle] = useState({ name: "", type: "ANNUAL", startDate: "", endDate: "", goalWeight: 70, competencyWeight: 30 });
  const [calCycle, setCalCycle] = useState("");
  const [cal, setCal] = useState(null);

  const isMgr = ["SUPERADMIN", "HR_ADMIN", "MANAGER"].includes(user?.role);
  const canManage = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);

  const loadDash = useCallback(() => api.get("/pms/dashboard").then(({ data }) => setDash(data)), []);
  const loadAppraisals = useCallback((scope) => api.get(`/pms/appraisals${scope ? `?scope=${scope}` : ""}`).then(({ data }) => setAppraisals(data)), []);
  const loadGoals = useCallback(() => api.get("/pms/goals").then(({ data }) => setGoals(data)), []);
  const loadCycles = useCallback(() => api.get("/pms/cycles").then(({ data }) => setCycles(data)), []);
  const loadFeedback = useCallback(() => api.get("/pms/feedback").then(({ data }) => setFeedback(data)), []);

  useEffect(() => {
    api.get("/pms/meta").then(({ data }) => setMeta(data));
    loadDash(); loadCycles();
  }, [loadDash, loadCycles]);

  useEffect(() => {
    if (tab === "my") loadAppraisals("me");
    else if (tab === "team") loadAppraisals(canManage ? undefined : "team");
    else if (tab === "goals") loadGoals();
    else if (tab === "feedback") loadFeedback();
  }, [tab, canManage, loadAppraisals, loadGoals, loadFeedback]);

  useEffect(() => { if (tab === "calibration" && calCycle) api.get(`/pms/cycles/${calCycle}/calibration`).then(({ data }) => setCal(data)); }, [tab, calCycle]);
  useEffect(() => { if (!calCycle && cycles.length) setCalCycle(cycles[0].id); }, [cycles, calCycle]);

  if (!meta || !dash) return <Loading />;

  const tabs = [
    ["dashboard", "Dashboard", LayoutDashboard, true],
    ["my", "My Review", ClipboardCheck, true],
    ["team", "My Team", Users, isMgr],
    ["goals", "Goals", Target, true],
    ["feedback", "Feedback", MessageCircle, true],
    ["cycles", "Cycles", CalendarRange, canManage],
    ["calibration", "Calibration", Scale, canManage],
  ].filter((t) => t[3]);

  const addFb = async (e) => { e.preventDefault(); try { await api.post("/pms/feedback", fb); alert("Feedback sent"); setFbOpen(false); setFb({ toId: "", content: "", rating: 5 }); loadFeedback(); } catch (err) { alert(apiError(err.response?.data?.detail)); } };
  const createCycle = async (e) => { e.preventDefault(); try { await api.post("/pms/cycles", newCycle); alert("Cycle created"); setCycleOpen(false); loadCycles(); } catch (err) { alert(apiError(err.response?.data?.detail)); } };
  const launch = async (id) => { if (!window.confirm("Launch this cycle? Appraisals will be created for all active employees.")) return; try { const { data } = await api.post(`/pms/cycles/${id}/launch`); alert(`Launched · ${data.created} appraisals created`); loadCycles(); loadDash(); } catch (e) { alert(apiError(e.response?.data?.detail)); } };
  const setStatus = async (id, status) => { try { await api.post(`/pms/cycles/${id}/status`, { status }); alert(`Cycle → ${status}`); loadCycles(); } catch (e) { alert(apiError(e.response?.data?.detail)); } };
  const updProgress = async (id, progress) => { await api.put(`/pms/goals/${id}`, { progress: +progress }); loadGoals(); };
  const refreshAll = () => { loadDash(); loadCycles(); if (tab === "my") loadAppraisals("me"); else if (tab === "team") loadAppraisals(canManage ? undefined : "team"); if (tab === "calibration" && calCycle) api.get(`/pms/cycles/${calCycle}/calibration`).then(({ data }) => setCal(data)); };

  return (
    <div>
      <PageHeader title="Performance" subtitle="Goals, appraisals, calibration & continuous feedback">
        <button data-testid="add-feedback" onClick={() => setFbOpen(true)} className="btn-outline"><Plus size={15} /> Feedback</button>
        <button data-testid="add-goal" onClick={() => setGoalOpen(true)} className="btn-accent"><Plus size={16} /> Goal</button>
      </PageHeader>

      <div className="mb-6 flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-800">
        {tabs.map(([k, l, Ic]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium ${tab === k ? "border-accent text-accent" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}><Ic size={15} />{l}</button>
        ))}
      </div>

      {/* DASHBOARD */}
      {tab === "dashboard" && (
        <div className="space-y-6" data-testid="pms-dashboard">
          {dash.activeCycle ? (
            <Card className="p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Active Cycle</p>
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">{dash.activeCycle.name}</h3>
                  <p className="text-xs text-slate-400">{dash.activeCycle.type} · {fmtDate(dash.activeCycle.startDate)} → {fmtDate(dash.activeCycle.endDate)}</p></div>
                <Badge value={dash.activeCycle.status} tone="ok" />
              </div>
              {dash.progress && (
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[["Total", dash.progress.total], ["Self Done", dash.progress.selfDone], ["Manager Done", dash.progress.managerDone], ["Calibrated", dash.progress.calibrated]].map(([l, v]) => (
                    <div key={l} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{l}</p><p className="font-mono text-2xl font-bold text-slate-800 dark:text-slate-100">{v}</p></div>
                  ))}
                </div>
              )}
            </Card>
          ) : <EmptyState icon={CalendarRange} title="No active review cycle" hint={canManage ? "Create one in the Cycles tab." : "Your HR team hasn't started a cycle yet."} />}

          <div className="grid gap-4 md:grid-cols-3">
            <Card className="p-5" data-testid="my-pending-card"><div className="flex items-center gap-3"><div className="rounded-lg bg-amber-100 p-2.5 text-amber-600 dark:bg-amber-500/15"><ClipboardCheck size={20} /></div><div><p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{dash.myPendingSelf || 0}</p><p className="text-xs text-slate-400">My self-appraisals pending</p></div></div></Card>
            {isMgr && <Card className="p-5"><div className="flex items-center gap-3"><div className="rounded-lg bg-violet-100 p-2.5 text-violet-600 dark:bg-violet-500/15"><Users size={20} /></div><div><p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{dash.myTeamPending || 0}</p><p className="text-xs text-slate-400">Team reviews awaiting me</p></div></div></Card>}
            <Card className="p-5"><div className="flex items-center gap-3"><div className="rounded-lg bg-sky-100 p-2.5 text-sky-600 dark:bg-sky-500/15"><Target size={20} /></div><div><p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{dash.myGoals || 0}</p><p className="text-xs text-slate-400">My goals</p></div></div></Card>
          </div>

          {canManage && dash.distribution && (
            <Card className="p-5"><h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200"><TrendingUp size={15} /> Final Rating Distribution</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={dash.distribution.map((x) => ({ ...x, label: meta.ratingScale.find((s) => s.value === x.rating)?.label || x.rating }))}>
                  <XAxis dataKey="rating" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} tick={{ fontSize: 12 }} /><Tooltip />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>{dash.distribution.map((x, i) => <Cell key={i} fill={RATE_COLORS[x.rating - 1]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
        </div>
      )}

      {/* MY REVIEW + TEAM (shared table) */}
      {(tab === "my" || tab === "team") && (
        appraisals.length === 0 ? <EmptyState icon={ClipboardCheck} title={tab === "my" ? "No appraisals for you yet" : "No team appraisals"} hint="They appear once a cycle is launched." /> : (
          <Card className="overflow-hidden"><div className="overflow-x-auto">
            <table className="w-full" data-testid="appraisals-table">
              <thead><tr><th className="th">Employee</th><th className="th">Cycle</th><th className="th">Stage</th><th className="th">Self</th><th className="th">Mgr</th><th className="th">Final</th><th className="th"></th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {appraisals.map((a) => (
                  <tr key={a.id} data-testid={`appraisal-row-${a.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <td className="td"><div className="flex items-center gap-2"><Avatar name={a.employeeName} size={28} /><span className="font-medium text-slate-800 dark:text-slate-100">{a.employeeName}</span></div></td>
                    <td className="td text-slate-500">{a.cycleName}</td>
                    <td className="td"><Badge value={a.stageLabel} tone={STAGE_TONE[a.stage]} /></td>
                    <td className="td font-mono">{a.selfScore ?? "—"}</td>
                    <td className="td font-mono">{a.mgrScore ?? "—"}</td>
                    <td className="td font-mono font-semibold">{a.finalRating ?? "—"}</td>
                    <td className="td text-right"><button onClick={() => setDrawerId(a.id)} data-testid={`open-appraisal-${a.id}`} className="btn-outline !px-3 !py-1 text-xs">Open</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div></Card>
        )
      )}

      {/* GOALS */}
      {tab === "goals" && (goals.length === 0 ? <EmptyState icon={Target} title="No goals set" /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {goals.map((g) => (
            <Card key={g.id} className="p-5" data-testid={`goal-${g.id}`}>
              <div className="flex items-start justify-between">
                <div><h3 className="font-semibold text-slate-800 dark:text-slate-100">{g.title}</h3>
                  <p className="text-xs text-slate-400">{g.employeeName} · {g.category} · weight {g.weightage}%{g.cycleId ? " · cycle" : ""}</p></div>
                <Badge value={g.status} />
              </div>
              {g.metric && <p className="mt-1.5 text-xs text-slate-500">Metric: {g.metric}</p>}
              {g.description && <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{g.description}</p>}
              <div className="mt-4 flex items-center gap-3">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"><div className="h-full rounded-full bg-accent" style={{ width: `${g.progress}%` }} /></div>
                <input type="range" min="0" max="100" step="10" value={g.progress} onChange={(e) => updProgress(g.id, e.target.value)} data-testid={`goal-progress-${g.id}`} className="w-24" />
                <span className="w-10 text-right font-mono text-sm font-semibold text-slate-700 dark:text-slate-200">{g.progress}%</span>
              </div>
            </Card>
          ))}
        </div>
      ))}

      {/* FEEDBACK */}
      {tab === "feedback" && (feedback.length === 0 ? <EmptyState icon={MessageCircle} title="No feedback yet" /> : (
        <div className="space-y-4">
          {feedback.map((f) => (
            <Card key={f.id} className="p-5" data-testid={`fb-${f.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3"><Avatar name={f.fromName} size={36} /><div><p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{f.fromName} → {f.toName}</p><p className="text-[11px] text-slate-400">{f.type} · {fmtDate(f.createdAt)}</p></div></div>
                <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} className={i <= (f.rating || 0) ? "text-amber-400" : "text-slate-300 dark:text-slate-600"} fill={i <= (f.rating || 0) ? "currentColor" : "none"} />)}</div>
              </div>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{f.content}</p>
            </Card>
          ))}
        </div>
      ))}

      {/* CYCLES */}
      {tab === "cycles" && (
        <div className="space-y-4" data-testid="cycles-list">
          <div className="flex justify-end"><button onClick={() => setCycleOpen(true)} className="btn-accent" data-testid="new-cycle-btn"><Plus size={16} /> New Cycle</button></div>
          {cycles.map((c) => (
            <Card key={c.id} className="p-5" data-testid={`cycle-${c.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div><h3 className="font-semibold text-slate-800 dark:text-slate-100">{c.name} <Badge value={c.status} tone={c.status === "ACTIVE" ? "ok" : c.status === "CLOSED" ? "neutral" : "warn"} /></h3>
                  <p className="text-xs text-slate-400">{c.type} · {fmtDate(c.startDate)} → {fmtDate(c.endDate)} · goals {c.goalWeight}% / competencies {c.competencyWeight}%</p></div>
                <div className="flex flex-wrap gap-2">
                  {c.status === "DRAFT" && <button onClick={() => launch(c.id)} className="btn-accent !py-1.5 text-xs" data-testid={`launch-${c.id}`}><Send size={14} /> Launch</button>}
                  {c.status === "ACTIVE" && <button onClick={() => setStatus(c.id, "CALIBRATION")} className="btn-outline !py-1.5 text-xs">Move to Calibration</button>}
                  {(c.status === "ACTIVE" || c.status === "CALIBRATION") && <button onClick={() => setStatus(c.id, "CLOSED")} className="btn-outline !py-1.5 text-xs">Close</button>}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                {[["Total", c.stats.total], ["Self", c.stats.selfDone], ["Manager", c.stats.managerDone], ["Calibrated", c.stats.calibrated]].map(([l, v]) => (
                  <div key={l} className="rounded-lg bg-slate-50 p-2 dark:bg-slate-800/50"><p className="text-[10px] uppercase tracking-wider text-slate-400">{l}</p><p className="font-mono text-lg font-bold text-slate-700 dark:text-slate-200">{v}</p></div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* CALIBRATION */}
      {tab === "calibration" && (
        <div className="space-y-5" data-testid="calibration-view">
          <div className="flex items-center gap-2"><label className="label !mb-0">Cycle</label>
            <select value={calCycle} onChange={(e) => setCalCycle(e.target.value)} data-testid="cal-cycle-select" className="input max-w-xs">{cycles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
          {cal && (<>
            <div className="grid gap-4 lg:grid-cols-2">
              <Card className="p-5"><h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">Rating Distribution</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={cal.distribution}><XAxis dataKey="rating" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} tick={{ fontSize: 12 }} /><Tooltip />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>{cal.distribution.map((x, i) => <Cell key={i} fill={RATE_COLORS[x.rating - 1]} />)}</Bar></BarChart>
                </ResponsiveContainer>
              </Card>
              <Card className="p-5"><h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">9-Box (calibrated)</h3>
                <div className="grid grid-cols-3 gap-2">
                  {[3, 2, 1].map((pot) => [1, 2, 3].map((perf) => (
                    <div key={`${perf}-${pot}`} className="flex min-h-[56px] flex-col items-center justify-center rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/40">
                      <span className="font-mono text-xl font-bold text-slate-700 dark:text-slate-200">{cal.nineBox[`${perf}-${pot}`] || 0}</span>
                      <span className="text-[9px] uppercase text-slate-400">P{pot}·R{perf}</span>
                    </div>
                  )))}
                </div>
                <p className="mt-2 text-center text-[10px] uppercase tracking-widest text-slate-400">performance →</p>
              </Card>
            </div>
            <Card className="overflow-hidden"><div className="overflow-x-auto">
              <table className="w-full" data-testid="calibration-table">
                <thead><tr><th className="th">Employee</th><th className="th">Manager</th><th className="th">Stage</th><th className="th">Self</th><th className="th">Mgr</th><th className="th">Final</th><th className="th">Potential</th><th className="th"></th></tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {cal.rows.map((r) => (
                    <tr key={r.id} data-testid={`cal-row-${r.id}`}>
                      <td className="td font-medium text-slate-800 dark:text-slate-100">{r.employeeName}</td>
                      <td className="td text-slate-500">{r.managerName || "—"}</td>
                      <td className="td"><Badge value={r.stageLabel} tone={STAGE_TONE[r.stage]} /></td>
                      <td className="td font-mono">{r.selfScore ?? "—"}</td>
                      <td className="td font-mono">{r.mgrScore ?? "—"}</td>
                      <td className="td font-mono font-semibold">{r.finalRating ?? "—"}</td>
                      <td className="td">{r.potential || "—"}</td>
                      <td className="td text-right"><button onClick={() => setDrawerId(r.id)} className="btn-outline !px-3 !py-1 text-xs" data-testid={`cal-open-${r.id}`}>Open</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div></Card>
          </>)}
        </div>
      )}

      {drawerId && <AppraisalDrawer id={drawerId} meta={meta} onClose={() => setDrawerId(null)} onSaved={refreshAll} />}
      <GoalModal open={goalOpen} onClose={() => setGoalOpen(false)} onSaved={() => { loadGoals(); }} meta={meta} cycles={cycles} />

      <Modal open={fbOpen} onClose={() => setFbOpen(false)} title="Give Feedback"
        footer={<><button onClick={() => setFbOpen(false)} className="btn-outline">Cancel</button><button form="fb-form" className="btn-accent">Send</button></>}>
        <form id="fb-form" onSubmit={addFb} className="space-y-4">
          <div><label className="label">To *</label><select required data-testid="fb-to" value={fb.toId} onChange={(e) => setFb({ ...fb, toId: e.target.value })} className="input"><option value="">— Select —</option>{meta.employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</select></div>
          <div><label className="label">Rating</label><select value={fb.rating} onChange={(e) => setFb({ ...fb, rating: +e.target.value })} className="input">{[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}</select></div>
          <div><label className="label">Feedback *</label><textarea required data-testid="fb-content" value={fb.content} onChange={(e) => setFb({ ...fb, content: e.target.value })} className="input h-24 py-2" /></div>
        </form>
      </Modal>

      <Modal open={cycleOpen} onClose={() => setCycleOpen(false)} title="New Review Cycle"
        footer={<><button onClick={() => setCycleOpen(false)} className="btn-outline">Cancel</button><button form="cycle-form" className="btn-accent">Create</button></>}>
        <form id="cycle-form" onSubmit={createCycle} className="space-y-4">
          <div><label className="label">Name *</label><input required data-testid="cycle-name" value={newCycle.name} onChange={(e) => setNewCycle({ ...newCycle, name: e.target.value })} className="input" placeholder="e.g. Annual Review 2026" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Type</label><select data-testid="cycle-type" value={newCycle.type} onChange={(e) => setNewCycle({ ...newCycle, type: e.target.value })} className="input"><option>ANNUAL</option><option>HALF_YEARLY</option><option>QUARTERLY</option></select></div>
            <div><label className="label">Goal / Competency %</label><div className="flex gap-2"><input type="number" data-testid="cycle-gw" value={newCycle.goalWeight} onChange={(e) => setNewCycle({ ...newCycle, goalWeight: +e.target.value, competencyWeight: 100 - +e.target.value })} className="input" /><input type="number" value={newCycle.competencyWeight} disabled className="input" /></div></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Start</label><input type="date" data-testid="cycle-start" value={newCycle.startDate} onChange={(e) => setNewCycle({ ...newCycle, startDate: e.target.value })} className="input" /></div>
            <div><label className="label">End</label><input type="date" data-testid="cycle-end" value={newCycle.endDate} onChange={(e) => setNewCycle({ ...newCycle, endDate: e.target.value })} className="input" /></div>
          </div>
          <p className="text-xs text-slate-400">All {meta.competencies.length} competencies are included by default. Launch the cycle after creation to generate appraisals.</p>
        </form>
      </Modal>
    </div>
  );
}
